import {
  Autocomplete,
  TextField,
  Checkbox,
  TextFieldProps,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  FormControlLabel,
} from "@mui/material";
import {
  Controller,
  FieldValues,
  Control,
  Path,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";
import { useEffect, useMemo, useState } from "react";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import { CreatingSearchHHForm } from "../constans";
import CheckIcon from "@mui/icons-material/Check";
import { useGetRegions } from "@src/entities/requestHH/model/hooks/useGetRegions/useGetRegions";
import { useGetFilters } from "@src/entities/requestHH/model/hooks/useGetFilters/useGetFilters";
import { VirtualizedListbox } from "@src/shared/ui";
import crossIcon from "@src/shared/assets/cross_icon.svg";
import openIcon from "@src/shared/assets/open_icon.svg";
import { moveImportantCitiesToTop } from "./utils";

import styles from "./SearchRegionField.module.scss";

interface TSearchRegionFieldProps<T extends FieldValues> {
  control: Control<T>;
  nameRegionAutocomplete: Path<T>;
  nameRegionCheckbox: Path<T>;
  nameWorkFormat: Path<T>;
  resumeRelocationName: Path<T>;
  label?: string;
  rules?: object;
  className?: string;
  slotProps?: TextFieldProps["slotProps"];
  helperText?: string;
  type?: React.HTMLInputTypeAttribute | undefined;
  disabled?: boolean;
  errors?: boolean | undefined;
  placeholder?: string;
  setValue: UseFormSetValue<CreatingSearchHHForm>;
  isDisabledFields: boolean;
  watch: UseFormWatch<CreatingSearchHHForm>;
}

export const crossImg = <img src={crossIcon} />;
export const openImg = <img src={openIcon} className={styles.openImg} />;

export const SearchRegionField = <T extends FieldValues>({
  control,
  nameRegionAutocomplete,
  nameRegionCheckbox,
  nameWorkFormat,
  label,
  placeholder,
  isDisabledFields,
  resumeRelocationName,
  setValue,
  watch,
}: TSearchRegionFieldProps<T>) => {
  const [expanded, setExpanded] = useState(true);
  const { data: regions } = useGetRegions();
  const { data: filters } = useGetFilters();
  // const counter = useRef(0);

  const REGIONS_OPTIONS = useMemo(
    () => moveImportantCitiesToTop(regions?.data || []),
    [regions?.data],
  );

  const { relocation_types, work_format } = filters?.data || {};

  const RELOCATION_TYPES_OPTIONS = relocation_types || [];
  const WORK_FORMAT_OPTIONS = work_format || [];
  // const WORK_SCHEDULE_OPTIONS = work_schedule || [];

  const handleToggle = () => {
    setExpanded((prev) => !prev);
  };

  const isDisabledAutocomplete = watch("isRussia");
  // const regionsOptions = watch("area");
  // const resumeRelocation = watch("resumeRelocation");

  useEffect(() => {
    if (isDisabledAutocomplete) {
      setValue("area", []);
    }
  }, [isDisabledAutocomplete]);

  // useEffect(() => {
  //   if (requestHHParams && counter.current <= 1) {
  //     counter.current += 1;
  //     return;
  //   }
  // }, [regionsOptions, requestHHParams]);

  return (
    <Accordion
      defaultExpanded
      expanded={expanded}
      sx={{
        mt: 0,
        backgroundColor: "#ffffff",
        borderRadius: "25px",
        padding: "17px 30px",
        color: "rgba(38, 38, 38, 1)",
        boxShadow: "none",
        "&:before": {
          display: "none",
        },
        "&.MuiAccordion-root": { marginTop: "10px" },
      }}
    >
      <AccordionSummary
        sx={{
          backgroundColor: "inherit",
          height: "30px",
          px: 0,
          "& .MuiAccordionSummary-content": {
            margin: 0,
          },
        }}
      >
        <Box
          onClick={handleToggle}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            cursor: "pointer",
            width: "100%",
            height: "30px",
            fontSize: "20px",
            fontWeight: 600,
          }}
        >
          <h3>Локация и формат</h3>
          <div className={styles.iconWrapper}>
            {expanded ? (
              <ExpandLess sx={{ color: "rgba(164, 187, 199, 1)" }} />
            ) : (
              <ExpandMore sx={{ color: "rgba(164, 187, 199, 1)" }} />
            )}
          </div>
        </Box>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          px: 0,
          backgroundColor: "inherit",
          py: 0,
          mt: 0,
          mb: 0,
          gap: 0,
          borderTop: "1px solid rgba(164, 187, 199, 0.25)",
          paddingTop: "20px",
        }}
      >
        <div className={styles.regionHeader}>
          <span className={styles.regionTitle}>Регион поиска:</span>
          <Controller
            name={nameRegionCheckbox}
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    {...field}
                    disabled={isDisabledFields}
                    checked={field.value}
                    icon={<span className={styles.customBox} />}
                    checkedIcon={
                      <span className={`${styles.customBox} ${styles.checked}`}>
                        <CheckIcon />
                      </span>
                    }
                  />
                }
                label="По всей России"
                className={styles.salaryLabel}
              />
            )}
          />
        </div>
        <Controller
          name={nameRegionAutocomplete}
          control={control}
          render={({ field: { onChange, value } }) => (
            <Autocomplete
              multiple
              options={REGIONS_OPTIONS}
              className={styles.autocomplete}
              disabled={isDisabledFields || isDisabledAutocomplete}
              disableCloseOnSelect
              clearIcon={crossImg}
              popupIcon={openImg}
              ListboxComponent={VirtualizedListbox}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, val) => option.id === val.id}
              noOptionsText="Введите название"
              value={value}
              onChange={(_, newValue) => onChange(newValue)}
              renderOption={(props, option, { selected }) => (
                <li {...props} key={`${option.name}_${option.id}`}>
                  <Checkbox
                    style={{ marginRight: 8 }}
                    checked={selected}
                    key={option.id}
                    disableRipple
                  />
                  {option.name}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={label}
                  placeholder={placeholder}
                  variant="outlined"
                  className={styles.inputField}
                  sx={{
                    borderRadius: "40px",
                    "& input": {
                      color: "rgba(38, 38, 38, 1)",

                      "&:-webkit-autofill": {
                        WebkitTextFillColor: "#fff",
                        caretColor: "rgba(38, 38, 38, 1)",
                        transition: "background-color 5000s ease-in-out 0s",
                      },
                    },
                    "& .MuiOutlinedInput-root": {
                      minHeight: 40,
                      alignItems: "center",
                      alignContent: "center",
                      display: "flex",
                      "& fieldset": {
                        borderColor: "rgba(164, 187, 199, 1)",
                        background: "rgba(164, 187, 199, 0.25)",
                      },
                      "&.Mui-disabled:hover fieldset": {
                        borderColor: "currentcolor",
                      },
                      "&:hover fieldset": {
                        borderColor: "rgba(164, 187, 199, 1)",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "rgba(164, 187, 199, 1)",
                      },
                    },
                    ".MuiFormHelperText-root.Mui-error": {
                      color: "rgba(255, 255, 255, 0.8)",
                    },
                    ".MuiFormHelperText-root.Mui-disabled": {
                      color: "rgba(255, 255, 255, 0.8)",
                      fontWeight: 400,
                    },
                  }}
                />
              )}
              sx={{
                mt: 2,
                ".MuiAutocomplete-popupIndicator": { color: "#fff" },
                ".MuiSvgIcon-root": { color: "#fff" },
                color: "#fff",
              }}
            />
          )}
        />
        <Controller
          name={resumeRelocationName}
          control={control}
          disabled={isDisabledFields}
          render={({ field: { onChange, value } }) => (
            <>
              <div className={styles.educationTitle}>
                <span>Формат проживания:</span>
              </div>
              <Autocomplete
                options={RELOCATION_TYPES_OPTIONS}
                className={styles.autocomplete}
                disableCloseOnSelect
                clearIcon={crossImg}
                popupIcon={openImg}
                noOptionsText="Такого варианта нет"
                getOptionLabel={(option) => {
                  return typeof option === "object" ? option.name : option;
                }}
                isOptionEqualToValue={(option, val) =>
                  option.hh_id === val.hh_id
                }
                value={value}
                disabled={isDisabledFields}
                onChange={(_, newValue) => onChange(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Укажите формат проживания"
                    variant="outlined"
                    className={styles.inputField}
                    sx={{
                      borderRadius: "40px",
                      "& input": {
                        color: "rgba(38, 38, 38, 1)",
                      },
                      "& .MuiOutlinedInput-root": {
                        background: "rgba(164, 187, 199, 0.25)",
                      },
                    }}
                  />
                )}
                sx={{
                  mt: 2,
                  ".MuiAutocomplete-popupIndicator": { color: "#fff" },
                  ".MuiSvgIcon-root": { color: "#fff" },
                  color: "#fff",
                }}
              />
            </>
          )}
        />
        <Controller
          name={nameWorkFormat}
          control={control}
          render={({ field: { onChange, value } }) => (
            <>
              <div className={styles.educationTitle}>
                <span>Формат работы:</span>
              </div>
              <Autocomplete
                multiple
                options={WORK_FORMAT_OPTIONS}
                className={styles.autocomplete}
                disableCloseOnSelect
                disabled={isDisabledFields}
                clearIcon={crossImg}
                popupIcon={openImg}
                getOptionLabel={(option) => {
                  return typeof option === "object" ? option.name : option;
                }}
                isOptionEqualToValue={(option, val) => option.id === val.id}
                value={value}
                onChange={(_, newValue) => onChange(newValue)}
                renderOption={(props, option, { selected }) => (
                  <li {...props} key={`${option.name}_${option.id}`}>
                    <Checkbox
                      style={{ marginRight: 8 }}
                      checked={selected}
                      key={option.id}
                      disableRipple
                    />
                    {option.name}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Укажите формат работы"
                    variant="outlined"
                    className={styles.inputField}
                    sx={{
                      borderRadius: "40px",
                      "& input": {
                        color: "rgba(38, 38, 38, 1)",
                      },
                      "& .MuiOutlinedInput-root": {
                        background: "rgba(164, 187, 199, 0.25)",
                      },
                    }}
                  />
                )}
                sx={{
                  mt: 2,
                  ".MuiAutocomplete-popupIndicator": { color: "#fff" },
                  ".MuiSvgIcon-root": { color: "#fff" },
                  color: "#fff",
                }}
              />
            </>
          )}
        />
      </AccordionDetails>
    </Accordion>
  );
};
